<script>

import appConfig from "@/app.config.json";
import Layout from "@/views/layouts/main.vue";
import CommonTableComponent from "@/components/table.vue";
import DatePicker from "vue2-datepicker";
import tableSearch from "@/mixins/tableSearch";
import pagination from "@/mixins/pagination";
import router from "@/mixins/router";
import datetime from "@/mixins/datetime";
import ModalNotificationInput from "./modals/ModalNotificationInput.vue";
import apiEndpoints from "@/services/apiEndpoints";
import user from "@/mixins/user";

export default {
  name: "notifications",
  page: {
    title: "お知らせ",
    meta: [{ name: "description", content: appConfig.description }]
  },
  mixins: [tableSearch, pagination, router, datetime, user],
  components: {
    Layout,
    DatePicker,
    CommonTableComponent,
    ModalNotificationInput
  },
  data() {
    return {
      data: [],
      isLoading: false,
      filters: {
        display_start_date: this.$route.query.display_start_date || "",
        display_end_date: this.$route.query.display_end_date || "",
        title: this.$route.query.title || "",
        column: this.$route.query.column || "id",
        dir: this.$route.query.dir || "DESC",
        page: this.$route.query.page || 1,
        searchToken: 0
      },
    };
  },
  created() {
      this.getData()
  },
  methods: {
    async getData() {
      this.isLoading = true;
      const response = await this.$http.get(apiEndpoints.search_discharger_site_notification, {
        ...this.filters,
      });
      const { status, data } = response;
      if (status) {
        const result = data.result;
        this.data = this.$_.isEmpty(result) ? [] : result.data;
        const meta = this.$_.isEmpty(data.result.meta) ? [] : data.result.meta;
        this.setMeta(meta);
      }
      this.isLoading = false;
    },
    search(){
      this.resetToFirstPage();
      this.updateUrlFilters();
    },
    onChangePage(event, page) {
      this.filters.page = page;
      this.updateUrlFilters();
    },
    onSelectData(user) {
      this.filters.user_name = user;
    },
    clearDataSearch(){
      for (const key in this.filters) {
        this.filters[key] = ""
      }
      this.filters.column = "id";
      this.filters.dir = "DESC";
      this.resetToFirstPage();
      this.updateUrlFilters();
    },
    back(){
      this.$router.push('/');
    },
    openModalNotificationInput(data = null) {
        this.$refs.modal_notification_input.openModal(data);
    },
    hanldeUpdateData() {
      this.search()
    },
    formatDate(date) {
      return date ? date.substring(0, 10) : ''
    },
  },
  computed: {
    headers() {
      return [
        {
          text: "ID",
          value: "id",
          sortable: true
        },
        {
          text: "表示日",
          value: "display_start_date",
          sortable: true
        },
        {
          text: "作成日時",
          value: "created_at",
          sortable: true
        },
        {
          text: "タイトル",
          value: "title",
          sortable: true
        },
        {
          text: "優先表示",
          value: "is_output_priority",
          sortable: true
        },
      ];
    },
    queryPage() {
      return this.$route.query;
    },
  },
  watch: {
    "filters.page": {
      immediate: false,
      handler(newVal, oldVal) {
        this.updateUrlFilters()
      }
    },
  }
}
</script>

<template>
  <Layout>
    <b-card>
      <div class="row mt-2">
        <div class="custom-from-to-date col-4 input-with-checkbox">
            <label for="">表示日</label>
            <div><date-picker
                lang="ja"
                placeholder="YYYY-MM-DD"
                format="YYYY-MM-DD"
                value-type="YYYY-MM-DD"
                position="left"
                v-model="filters.display_start_date"
            ></date-picker></div>
            <div class="align-self-center">～</div>
            <div>
                <date-picker
                    lang="ja"
                    placeholder="YYYY-MM-DD"
                    format="YYYY-MM-DD"
                    value-type="YYYY-MM-DD"
                    position="left"
                    v-model="filters.display_end_date"
                ></date-picker>
            </div>
        </div>
      </div>
      <div class="row mt-2">
        <b-col cols="3">
          <div class="custom-input gap-2">
            <label for="">タイトル</label>
            <b-form-input name="title" v-model="filters.title" type="text"></b-form-input>
          </div>
        </b-col>
      </div>
      <b-row>
        <div class="d-flex justify-content-center align-items-center">
          <b-button size="lg" variant="primary" @click="search" class="me-2">
            <div class="d-flex align-items-center justify-content-center">
              <i class="fas fa-search"></i> {{ this.$lang.button.search }}
            </div>
          </b-button>
          <b-button @click="clearDataSearch" variant="dark" size="lg" class="me-2">
            <i class="fas fa-eraser"></i> {{ this.$lang.button.clear }}
          </b-button>
        </div>
      </b-row>
      <b-button
        v-if="canUserEdit()"
        @click="openModalNotificationInput" 
        variant="primary"
        class="btn btn-primary w-md waves-effect waves-light">
        <i class="fas fa-plus"></i>
        お知らせを追加する
      </b-button>

    </b-card>
    <b-overlay
      :show="isLoading"
      rounded
      opacity="0.6"
      spinner-small
      spinner-variant="primary"
      class="d-inline-block w-100"
    >
      <b-card>
        <CommonTableComponent :height-class="`table-wrapper-500`">
          <thead class="table-light">
          <tr>
            <th
              v-for="(item, index) in headers"
              :key="index"
              :class="getHeaderClass(item)"
            >
              <span v-if="item.sortable" @click="clickSort(item.value)">
                {{ item.text }}
                <i :class="getSortClass(item.value)"></i>
              </span>
              <span v-else>
                {{ item.text }}
              </span>
            </th>
          </tr>
          </thead>
          <tbody>
            <!-- open modal here to edit record -->
            <tr
                class="cursor-pointer"
                v-for="(item, index) in data"
                @click="openModalNotificationInput(item)"
            >
            <td>{{ item.id }}</td>
            <td>
              {{ item.display_start_date | longDate }}
              ～
              {{ item.display_end_date | longDate }}
            </td>
            <td>{{ formatDate(item.created_at) }}</td>
            <td>{{ item.title }}</td>
            <td>{{ item.is_output_priority ? 'する' : '' }}</td>
            </tr>
          <tr v-if="!data.length && !isLoading">
            <td colspan="13" class="text-center">
              データなし。
            </td>
          </tr>
          </tbody>
        </CommonTableComponent>
        <div
          class="d-flex justify-content-between mt-3"
          v-if="data.length > 0"
        >
          <div class="d-flex align-items-center">
            <b-pagination
              v-model="filters.page"
              :total-rows="meta.total"
              :per-page="meta.per_page"
            ></b-pagination>
          </div>
          <div class="d-flex align-items-center">
            <div class="me-2">
              {{ meta.from }} - {{ meta.to }} / {{ meta.total }}
            </div>
          </div>
        </div>
      </b-card>

      <ModalNotificationInput @onUpdateData="hanldeUpdateData" ref="modal_notification_input"/>
    </b-overlay>
  </Layout>
</template>

<style scoped>
</style>
